"use client";

import { useEffect, useRef, useState } from "react";

interface CountUpProps {
  end: number;
  duration?: number; // in ms
  className?: string;
}

export const CountUp = ({ end, duration = 2000, className }: CountUpProps) => {
  const [count, setCount] = useState(0);
  const startTimestamp = useRef<number | null>(null);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    const step = (timestamp: number) => {
      if (!startTimestamp.current) startTimestamp.current = timestamp;
      const progress = timestamp - startTimestamp.current;
      const progressRatio = Math.min(progress / duration, 1);
      const current = Math.floor(progressRatio * end);

      setCount(current);

      if (progress < duration) {
        animationRef.current = requestAnimationFrame(step);
      } else {
        setCount(end);
      }
    };

    // Start animation only once per `end` or `duration` change
    animationRef.current = requestAnimationFrame(step);

    // Cleanup to cancel animation when component unmounts or props change
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      startTimestamp.current = null;
    };
  }, [end, duration]);

  return <span className={className}>{count.toLocaleString()}</span>;
};
